import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthSettingsService } from '../auth-settings.service';
import {
  AccordionComponent,
  AccordionItemComponent,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  FormField,
  HelpDialogService,
  Input,
  LoadingStateComponent,
  NumberInput,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  ToastService,
  TooltipDirective,
  ZagButton,
  ZagIcon,
  ZagSwitch,
  type ProfileStat,
} from '@zag/ui';
import { type HelpTopicId } from '@zag/ai/models';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, catchError, of } from 'rxjs';

interface MaskedAuthConfig {
  secret?: string;
  emailAndPassword?: {
    enabled?: boolean;
    credentialIdentifier?: 'email' | 'username';
    minUsernameLength?: number;
    maxUsernameLength?: number;
    requireEmailVerification?: boolean;
    minPasswordLength?: number;
    maxPasswordLength?: number;
  };
  socialProviders?: Record<
    string,
    {
      clientId?: string;
      clientSecret?: string;
      tenantId?: string;
    } | null
  >;
}

type ProviderStatus = 'empty' | 'partial' | 'complete';
type SocialProvider = 'microsoft' | 'google' | 'apple' | 'linkedin';

const SOCIAL_PROVIDERS: SocialProvider[] = ['microsoft', 'google', 'apple', 'linkedin'];

// Form control name -> `settings.auth.socialProviders.*` label key
const PROVIDER_FIELDS: Record<SocialProvider, Record<string, string>> = {
  microsoft: { microsoftTenantId: 'tenantId', microsoftClientId: 'clientId', microsoftClientSecret: 'clientSecret' },
  google: { googleClientId: 'clientId', googleClientSecret: 'clientSecret' },
  apple: { appleClientId: 'clientId', appleClientSecret: 'clientSecret' },
  linkedin: { linkedinClientId: 'clientId', linkedinClientSecret: 'clientSecret' },
};

type ProviderFill = { status: 'empty' | 'complete' } | { status: 'partial'; missing: string[] };

// A saved secret only counts as filled while another field is filled: clearing every field removes the provider
function filledFields(
  fields: string[],
  valueOf: (field: string) => string | null | undefined,
  secretField?: string,
  hasSavedSecret = false
): string[] {
  const filled = fields.filter(field => !!valueOf(field)?.trim());
  const editingSavedProvider = hasSavedSecret && filled.some(field => field !== secretField);
  const secretCountsAsFilled = !!secretField && fields.includes(secretField) && !filled.includes(secretField) && editingSavedProvider;
  return secretCountsAsFilled ? [...filled, secretField] : filled;
}

/** @internal Exported for unit testing only. */
export function allOrNoneValidator(
  fields: string[],
  secretField?: string,
  hasSavedSecret?: () => boolean
): (group: AbstractControl) => ValidationErrors | null {
  return (group: AbstractControl): ValidationErrors | null => {
    const filled = filledFields(fields, field => group.get(field)?.value as string | null | undefined, secretField, hasSavedSecret?.());
    return filled.length === 0 || filled.length === fields.length ? null : { providerPartial: true };
  };
}

@Component({
  selector: 'sc-auth-settings',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    FormField,
    Input,
    ZagSwitch,
    NumberInput,
    ZagButton,
    ZagIcon,
    TooltipDirective,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './auth-settings.component.html',
  styleUrl: './auth-settings.component.scss',
})
export class AuthSettingsComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authSettings = inject(AuthSettingsService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly helpDialog = inject(HelpDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly isSaving = signal(false);

  protected readonly profileName = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.auth.title');
  });
  protected readonly profileSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.auth.description');
  });

  protected readonly settingsForm = this.fb.group({
    // Email & Password
    emailAndPasswordEnabled: [true],
    usernameEnabled: [false],
    minPasswordLength: [8 as number | null, [Validators.min(6), Validators.max(128)]],
    maxPasswordLength: [128 as number | null, [Validators.min(8), Validators.max(256)]],
    requireEmailVerification: [false],
    // Social providers — no allowedDomains
    microsoftTenantId: [''],
    microsoftClientId: [''],
    microsoftClientSecret: [''],
    googleClientId: [''],
    googleClientSecret: [''],
    appleClientId: [''],
    appleClientSecret: [''],
    linkedinClientId: [''],
    linkedinClientSecret: [''],
  });

  // Attach cross-field group validators so the form is invalid when partially filled.
  // The hasSavedSecret callbacks are evaluated at validation time, so they correctly
  // reflect the state set by populateForm() after the config is loaded.
  constructor() {
    this.settingsForm.addValidators(
      SOCIAL_PROVIDERS.map(p => allOrNoneValidator(Object.keys(PROVIDER_FIELDS[p]), `${p}ClientSecret`, () => this.savedSecrets()[p]))
    );
  }

  // Use toSignal so computed signals react to form value changes
  private readonly formValue = toSignal(this.settingsForm.valueChanges, {
    initialValue: this.settingsForm.value,
  });

  private providerFill(provider: SocialProvider): ProviderFill {
    const values = this.formValue() as Record<string, string | null | undefined>;
    const fieldLabels = PROVIDER_FIELDS[provider];
    const fields = Object.keys(fieldLabels);
    const filled = filledFields(fields, field => values[field], `${provider}ClientSecret`, this.savedSecrets()[provider]);
    if (filled.length === fields.length) return { status: 'complete' };
    if (filled.length === 0) return { status: 'empty' };
    return { status: 'partial', missing: fields.filter(field => !filled.includes(field)).map(field => fieldLabels[field]) };
  }

  private incompleteTooltip(provider: SocialProvider): string | null {
    this.currentLang();
    const fill = this.providerFill(provider);
    if (fill.status !== 'partial') return null;
    const fields = fill.missing.map(label => this.translate.instant(`settings.auth.socialProviders.${label}`)).join(', ');
    return this.translate.instant('settings.auth.socialProviders.incomplete', { fields });
  }

  protected readonly microsoftStatus = computed(() => this.providerFill('microsoft').status);
  protected readonly googleStatus = computed(() => this.providerFill('google').status);
  protected readonly appleStatus = computed(() => this.providerFill('apple').status);
  protected readonly linkedinStatus = computed(() => this.providerFill('linkedin').status);

  protected readonly microsoftStatusIcon = computed(() => statusToIcon(this.microsoftStatus()));
  protected readonly googleStatusIcon = computed(() => statusToIcon(this.googleStatus()));
  protected readonly appleStatusIcon = computed(() => statusToIcon(this.appleStatus()));
  protected readonly linkedinStatusIcon = computed(() => statusToIcon(this.linkedinStatus()));

  protected readonly microsoftStatusTooltip = computed(() => this.incompleteTooltip('microsoft'));
  protected readonly googleStatusTooltip = computed(() => this.incompleteTooltip('google'));
  protected readonly appleStatusTooltip = computed(() => this.incompleteTooltip('apple'));
  protected readonly linkedinStatusTooltip = computed(() => this.incompleteTooltip('linkedin'));

  /** Returns ●●●●●● when a secret is already saved on the server, empty otherwise. */
  protected secretPlaceholder(hasSaved: boolean): string {
    return hasSaved ? '••••••••' : '';
  }

  protected openProviderHelp(provider: SocialProvider): void {
    this.helpDialog.openTopic(`socialLogin.${provider}` as HelpTopicId);
  }

  protected readonly profileStats = computed<ProfileStat[]>(() => {
    this.currentLang();
    const configured = [this.microsoftStatus(), this.googleStatus(), this.appleStatus(), this.linkedinStatus()].filter(
      s => s === 'complete'
    ).length;

    return [
      {
        label: this.translate.instant('settings.auth.stats.providers'),
        value: String(configured),
        icon: 'key',
      },
    ];
  });

  protected readonly configResource = rxResource<MaskedAuthConfig | null, void>({
    stream: () =>
      this.authSettings.getConfig<MaskedAuthConfig>().pipe(
        map(response => {
          this.populateForm(response);
          return response;
        }),
        catchError(() => {
          this.toast.error(this.translate.instant('settings.auth.loadError'));
          return of(null);
        })
      ),
  });

  // Tracks whether each provider has a client secret already saved on the server.
  // The server returns null for clientSecret and sets hasClientSecret: boolean.
  protected readonly savedSecrets = signal<Record<SocialProvider, boolean>>({
    microsoft: false,
    google: false,
    apple: false,
    linkedin: false,
  });

  private populateForm(config: MaskedAuthConfig & { socialProviders?: Record<string, { hasClientSecret?: boolean } | null> }): void {
    const ep = config.emailAndPassword ?? {};
    const sp = config.socialProviders ?? {};

    // Track which providers have a secret stored on the server
    this.savedSecrets.set({
      microsoft: !!(sp['microsoft'] as { hasClientSecret?: boolean } | null)?.hasClientSecret,
      google: !!(sp['google'] as { hasClientSecret?: boolean } | null)?.hasClientSecret,
      apple: !!(sp['apple'] as { hasClientSecret?: boolean } | null)?.hasClientSecret,
      linkedin: !!(sp['linkedin'] as { hasClientSecret?: boolean } | null)?.hasClientSecret,
    });

    // Secret fields are always left blank — typing a new value replaces it,
    // leaving blank preserves the existing secret (backend detects null/empty = keep).
    this.settingsForm.patchValue({
      emailAndPasswordEnabled: ep.enabled ?? true,
      usernameEnabled: ep.credentialIdentifier === 'username',
      minPasswordLength: ep.minPasswordLength ?? 8,
      maxPasswordLength: ep.maxPasswordLength ?? 128,
      requireEmailVerification: ep.requireEmailVerification ?? false,
      microsoftTenantId: sp['microsoft']?.tenantId ?? '',
      microsoftClientId: sp['microsoft']?.clientId ?? '',
      microsoftClientSecret: '',
      googleClientId: sp['google']?.clientId ?? '',
      googleClientSecret: '',
      appleClientId: sp['apple']?.clientId ?? '',
      appleClientSecret: '',
      linkedinClientId: sp['linkedin']?.clientId ?? '',
      linkedinClientSecret: '',
    });
  }

  protected onSave(): void {
    if (!this.settingsForm.valid) return;
    this.isSaving.set(true);

    const v = this.settingsForm.value;

    /**
     * Build a single provider's payload.
     * - All credential fields empty AND no saved secret → null (remove from config)
     * - Otherwise → return the object. clientSecret is null when blank (backend preserves).
     */
    const buildProvider = (
      nonSecretFields: Record<string, string | undefined>,
      newSecret: string | undefined
    ): Record<string, string | null> | null | undefined => {
      const nonSecretValues = Object.values(nonSecretFields).map(f => f?.trim() ?? '');
      const anyNonSecretFilled = nonSecretValues.some(v => v.length > 0);
      const trimmedNew = newSecret?.trim() ?? '';

      // null means "preserve existing" on the backend
      const resolvedSecret: string | null = trimmedNew || null;

      // Remove the provider when all non-secret fields are cleared and no new secret is
      // entered — this covers the "clear provider" case regardless of a previously-saved secret.
      if (!anyNonSecretFilled && !trimmedNew) return null;

      return { ...nonSecretFields, clientSecret: resolvedSecret } as Record<string, string | null>;
    };

    const socialProviders: Record<string, Record<string, string | null> | null> = {};

    const microsoft = buildProvider(
      { tenantId: v.microsoftTenantId ?? '', clientId: v.microsoftClientId ?? '' },
      v.microsoftClientSecret ?? undefined
    );
    if (microsoft !== undefined) socialProviders['microsoft'] = microsoft;

    const google = buildProvider({ clientId: v.googleClientId ?? '' }, v.googleClientSecret ?? undefined);
    if (google !== undefined) socialProviders['google'] = google;

    const apple = buildProvider({ clientId: v.appleClientId ?? '' }, v.appleClientSecret ?? undefined);
    if (apple !== undefined) socialProviders['apple'] = apple;

    const linkedin = buildProvider({ clientId: v.linkedinClientId ?? '' }, v.linkedinClientSecret ?? undefined);
    if (linkedin !== undefined) socialProviders['linkedin'] = linkedin;

    const hasSocial = Object.values(socialProviders).some(p => p !== null);
    const hasEmailPassword = !!v.emailAndPasswordEnabled;
    const authMode: string[] = [];
    if (hasEmailPassword) authMode.push('emailPassword');
    if (hasSocial) authMode.push('social');

    const payload = {
      authMode,
      emailAndPassword: {
        enabled: v.emailAndPasswordEnabled,
        credentialIdentifier: v.usernameEnabled ? 'username' : 'email',
        minPasswordLength: v.minPasswordLength,
        maxPasswordLength: v.maxPasswordLength,
        requireEmailVerification: v.requireEmailVerification,
      },
      socialProviders,
    };

    this.authSettings
      .patchConfig<MaskedAuthConfig>(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.toast.success(this.translate.instant('settings.auth.saveSuccess'));
          this.configResource.reload();
        },
        error: () => {
          this.isSaving.set(false);
          this.toast.error(this.translate.instant('settings.auth.saveError'));
        },
      });
  }
}

function statusToIcon(status: ProviderStatus): string | null {
  if (status === 'complete') return 'check_circle';
  if (status === 'partial') return 'warning';
  return null;
}
